/**
 * Inline play/pause for Shopify-hosted <video> in collapsible side media.
 * Load after theme-video-progress-clip.js (defines `theme-video-playback-progress` and window.themeVideoProgressUi).
 */
(function () {
  "use strict";

  if (customElements.get("collapsible-side-media-video")) {
    return;
  }

  class CollapsibleSideMediaVideo extends HTMLElement {
    constructor() {
      super();
      this.videoElement = null;
      this.revealSurfaceElement = null;
      this.controlElement = null;
      this.revealClassObserver = null;
      this.boundVideoEnded = this.onVideoEnded.bind(this);
      this.onControlClickBound = this.onControlClick.bind(this);
      this.autoplayOnReveal = true;
      this.loopWithoutControls = false;
    }

    connectedCallback() {
      this.autoplayOnReveal = this.dataset.autoplayOnReveal !== "false";
      this.loopWithoutControls = this.dataset.loopWithoutControls === "true";

      this.videoElement = this.querySelector("video");
      this.revealSurfaceElement = this.closest(".revealed-image");
      this.controlElement = this.querySelector("[data-collapsible-side-media-toggle]");

      if (this.controlElement) {
        this.controlElement.addEventListener("click", this.onControlClickBound);
      }

      if (this.videoElement) {
        this.videoElement.addEventListener("ended", this.boundVideoEnded);
      }

      if (this.revealSurfaceElement) {
        this.revealClassObserver = new MutationObserver(() => this.onRevealSurfaceClassChange());
        this.revealClassObserver.observe(this.revealSurfaceElement, {
          attributes: true,
          attributeFilter: ["class"]
        });
        this.onRevealSurfaceClassChange();
      }
    }

    disconnectedCallback() {
      if (this.revealClassObserver) {
        this.revealClassObserver.disconnect();
        this.revealClassObserver = null;
      }
      if (this.controlElement) {
        this.controlElement.removeEventListener("click", this.onControlClickBound);
      }
      if (this.videoElement) {
        this.videoElement.removeEventListener("ended", this.boundVideoEnded);
      }
    }

    onRevealSurfaceClassChange() {
      const isRevealed = this.revealSurfaceElement && this.revealSurfaceElement.classList.contains("reveal");
      if (!isRevealed) {
        this.pauseMedia();
        return;
      }
      const shouldAutostartMuted = this.loopWithoutControls || this.autoplayOnReveal;
      if (shouldAutostartMuted) {
        this.autoplayMutedOnReveal();
      }
    }

    autoplayMutedOnReveal() {
      if (!this.videoElement) {
        return;
      }
      this.videoElement.muted = true;
      if (this.videoElement.ended) {
        this.videoElement.currentTime = 0;
      }
      this.videoElement.play().catch(() => {});
      if (this.controlElement) {
        this.controlElement.classList.add("is-playing");
        this.controlElement.setAttribute("aria-label", "Pause video");
      }
    }

    onVideoEnded() {
      if (this.loopWithoutControls) {
        return;
      }
      this.resetPlaybackUiToPlayState();
    }

    onControlClick(event) {
      event.preventDefault();
      if (!this.videoElement) {
        return;
      }
      if (this.videoElement.paused) {
        this.playMedia();
      } else {
        this.pauseMedia();
      }
    }

    resetPlaybackUiToPlayState() {
      if (!this.videoElement) {
        return;
      }
      if (this.controlElement) {
        this.controlElement.classList.remove("is-playing");
        this.controlElement.setAttribute("aria-label", "Play video");
      }
    }

    playMedia() {
      if (!this.videoElement) {
        return;
      }
      if (this.videoElement.ended) {
        this.videoElement.currentTime = 0;
      }
      this.videoElement.muted = false;
      this.videoElement.play().catch(() => {});
      if (this.controlElement) {
        this.controlElement.classList.add("is-playing");
        this.controlElement.setAttribute("aria-label", "Pause video");
      }
    }

    pauseMedia() {
      if (!this.videoElement) {
        return;
      }
      this.videoElement.pause();
      this.resetPlaybackUiToPlayState();
    }
  }

  customElements.define("collapsible-side-media-video", CollapsibleSideMediaVideo);
})();
